// src/components/achievements/CelebrationPopup.js
import React, { useEffect, useRef, useState } from 'react';
import { db } from '../../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const CONFETTI_COLORS = ['#ff6b6b','#ffd93d','#6bcb77','#4d96ff','#ff6bff','#ff9f43'];

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  if (!text) return y;
  const words = text.split(' ');
  let line = '';
  let currentY = y;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, currentY);
      line = words[i] + ' ';
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), x, currentY);
  return currentY + lineHeight;
};

const CelebrationPopup = ({ achievement, userData, language = 'mr', onClose }) => {
  const canvasRef = useRef();
  const [cardImageUrl, setCardImageUrl] = useState(null);
  const [confetti, setConfetti] = useState([]);
  const [shareFailedPrompt, setShareFailedPrompt] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const t = language === 'mr' ? {
    share: 'शेअर करा',
    download: 'डाउनलोड करा',
    shareFailed: 'शेअर करता आलं नाही. डाउनलोड करायचं आहे का?',
    yes: 'हो', no: 'नाही',
    downloadSuccess: 'फोटो डाउनलोड झाला! WhatsApp वर शेअर करा 🎉'
  } : {
    share: 'Share',
    download: 'Download',
    shareFailed: "Couldn't share. Download instead?",
    yes: 'Yes', no: 'No',
    downloadSuccess: 'Photo downloaded! Share on WhatsApp 🎉'
  };

  useEffect(() => {
    const audio = new Audio('/claps.mp3');
    audio.volume = 0.8;
    audio.play().catch(() => {});

    const pieces = [];
    for (let i = 0; i < 60; i++) {
      pieces.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: 2.5 + Math.random() * 1.5,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        size: 6 + Math.random() * 6,
        rotation: Math.random() * 360
      });
    }
    setConfetti(pieces);

    return () => { audio.pause(); };
  }, []);

  useEffect(() => {
    const generate = async () => {
      try {
        const templateSnap = await getDoc(doc(db, 'achievementTemplates', achievement.category));
        const templateURL = templateSnap.exists() ? templateSnap.data().imageURL : null;
        if (!templateURL) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = 1080;
        canvas.height = 1440;

        await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => { ctx.drawImage(img, 0, 0, 1080, 1440); resolve(); };
          img.onerror = reject;
          img.src = templateURL;
        });

        if (userData?.photoURL) {
          await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const cx = 540, cy = 450, r = 230;
              ctx.save();
              ctx.beginPath();
              ctx.arc(cx, cy, r, 0, Math.PI * 2);
              ctx.clip();
              const side = Math.min(img.width, img.height);
              const sx = (img.width - side) / 2;
              const sy = (img.height - side) / 2;
              ctx.drawImage(img, sx, sy, side, side, cx - r, cy - r, r * 2, r * 2);
              ctx.restore();
              ctx.beginPath();
              ctx.arc(cx, cy, r, 0, Math.PI * 2);
              ctx.strokeStyle = 'white';
              ctx.lineWidth = 12;
              ctx.stroke();
              resolve();
            };
            img.onerror = resolve;
            img.src = userData.photoURL;
          });
        }

        const firstName = userData?.firstName || userData?.name?.split(' ')[0] || '';
        const lastName = userData?.lastName || userData?.name?.split(' ').slice(1).join(' ') || '';
        const fullName = `${firstName} ${lastName}`.trim();
        ctx.fillStyle = 'white';
        ctx.font = 'bold 72px Arial';
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 8;
        ctx.fillText(fullName, 540, 790);
        ctx.shadowBlur = 0;

        const title = achievement.title?.[language] || achievement.title?.mr || achievement.title?.en || '';
        ctx.font = 'bold 60px Arial';
        ctx.fillStyle = 'white';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 6;
        wrapText(ctx, title, 540, 1080, 900, 72);
        ctx.shadowBlur = 0;

        const description = achievement.description?.[language] || achievement.description?.mr || achievement.description?.en || '';
        ctx.font = '50px Arial';
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.shadowColor = 'rgba(0,0,0,0.4)';
        ctx.shadowBlur = 4;
        wrapText(ctx, description, 540, 1200, 700, 64);
        ctx.shadowBlur = 0;

        setCardImageUrl(canvas.toDataURL('image/png'));
      } catch (e) {}
    };
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achievement]);

  const handleDownload = () => {
    if (!cardImageUrl) return;
    const link = document.createElement('a');
    link.href = cardImageUrl;
    link.download = 'spocademy_achievement.png';
    link.click();
    setDownloadSuccess(true);
  };

  const handleShare = async () => {
    if (!cardImageUrl) return;
    try {
      const res = await fetch(cardImageUrl);
      const blob = await res.blob();
      const file = new File([blob], 'spocademy_achievement.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        setShareFailedPrompt(true);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setShareFailedPrompt(true);
      }
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden'
    }}>
      <style>{`
        @keyframes celebration-confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
        }
      `}</style>

      {confetti.map(p => (
        <div key={p.id} style={{
          position: 'absolute', top: 0, left: `${p.left}%`,
          width: p.size, height: p.size * 0.6,
          background: p.color,
          animation: `celebration-confetti-fall ${p.duration}s linear ${p.delay}s infinite`,
          transform: `rotate(${p.rotation}deg)`
        }} />
      ))}

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div style={{
        position: 'relative', maxWidth: 340, width: '90%',
        background: '#fff', borderRadius: 16, padding: 16,
        textAlign: 'center', zIndex: 2
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 8, right: 8, border: 'none',
            background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#888'
          }}
        >✕</button>

        {cardImageUrl ? (
          <img src={cardImageUrl} alt="Achievement" style={{ width: '100%', borderRadius: 10 }} />
        ) : (
          <div style={{ padding: '60px 0', color: '#888' }}>...</div>
        )}

        {downloadSuccess ? (
          <div style={{ marginTop: 12, color: '#28a745', fontWeight: 600, fontSize: 14 }}>
            {t.downloadSuccess}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, marginTop: 12, justifyContent: 'center' }}>
            <button
              onClick={handleDownload}
              style={{
                width: 140, background: 'transparent', border: '2px solid #007fff',
                color: '#007fff', borderRadius: 8, padding: '10px 8px',
                fontWeight: 600, whiteSpace: 'nowrap'
              }}
            >
              {t.download}
            </button>
            <button
              onClick={handleShare}
              style={{
                width: 140, background: '#007fff', border: 'none', color: '#fff',
                borderRadius: 8, padding: '10px 8px', fontWeight: 600, whiteSpace: 'nowrap'
              }}
            >
              {t.share}
            </button>
          </div>
        )}

        {shareFailedPrompt && (
          <div style={{ marginTop: 12, padding: 12, background: '#f8f9fa', borderRadius: 10 }}>
            <p style={{ fontSize: 14, marginBottom: 8 }}>{t.shareFailed}</p>
            <button
              onClick={() => { setShareFailedPrompt(false); handleDownload(); }}
              style={{ marginRight: 8, padding: '6px 14px', borderRadius: 6, border: 'none', background: '#007fff', color: '#fff' }}
            >{t.yes}</button>
            <button
              onClick={() => setShareFailedPrompt(false)}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', background: '#fff' }}
            >{t.no}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CelebrationPopup;